import { useContext, useState, useEffect, useMemo } from 'react'
import blocksToHtml from '@sanity/block-content-to-html'

import {
  SanityProductFragment,
  SanityProductVariantFragment,
} from '@data/sanity/queries/types/product'
import { SanitySiteFragment } from '@data/sanity/queries/types/site'
import { getPrice, getFirstValue } from '@lib/helpers'
import { getSanityImageUrl } from '@lib/image'
import { getProductGalleryPhotos } from '@lib/product'
import {
  ReviewOptions,
  ReviewProvider,
  getProductReviews,
  ProductReview,
  getProductReviewSummary,
} from '@lib/review'
import { getPageUrl, PageType } from './routes'
import { ShopContext } from '@lib/shop'
import { SanityBlogPostPage } from '@data/sanity/queries/types/blog'
import {
  SanityBlogPostBody,
  SanityModuleType,
} from '@data/sanity/queries/types/modules'
import {
  SanityContentInfoBox,
  SanityContentQuote,
} from '@data/sanity/queries/types/content'
import { SanityImageFragment } from '@data/sanity/queries/types/image'

interface AuthorSchema {
  '@type': 'Person' | 'Organization'
  name?: string
  url?: string
}

interface BrandSchema {
  '@type': 'Brand'
  name: string
}

interface ImageSchema {
  '@type': 'ImageObject'
  '@id': string
  url: string
  width?: string
  height?: string
}

interface OfferSchema {
  '@type': 'Offer'
  url: string
  availability: string
  price?: string
  priceCurrency?: string
  priceValidUntil?: string
}

interface ReviewSchema {
  '@type': 'Review'
  reviewRating: {
    '@type': 'Rating'
    ratingValue?: number
  }
  author: string | AuthorSchema
  reviewBody?: string
}

interface AggregateRatingSchema {
  '@type': 'AggregateRating'
  ratingCount: string
  ratingValue: string
}

interface ProductSchema {
  '@context': 'https://schema.org'
  '@type': 'Product'
  name: string
  description?: string
  sku?: string
  gtin?: string
  brand: BrandSchema
  image?: string | string[] | ImageSchema | ImageSchema[]
  offers?: OfferSchema
  review?: ReviewSchema[]
  aggregateRating?: AggregateRatingSchema
}

interface Rating {
  reviews?: ReviewSchema[]
  aggregateRating?: AggregateRatingSchema
}

interface BlogPostSchema {
  '@context': 'https://schema.org'
  '@type': 'BlogPosting'
  url: string
  headline: string
  datePublished: Date
  dateModified: Date
  author?: string | AuthorSchema
  keywords?: string[]
  abstract?: string
  articleBody?: string
  thumbnailUrl?: string
  image?: string | string[] | ImageSchema | ImageSchema[]
}

// @ts-expect-error TODO: Add missing Hyperscript type
const h = blocksToHtml.h

export const getBlogPostSchema = (
  {
    title: headline,
    excerpt: abstract,
    slug,
    author,
    date,
    modifiedDate,
    categories,
    coverImage,
    modules,
    seo,
  }: SanityBlogPostPage,
  site: SanitySiteFragment
) => {
  const domain = site.settings.siteURL.replace(/\/+$/g, '')
  const blogPostUrl = getPageUrl(PageType.BLOG_POST, slug)

  const blogPostBodyModule = modules?.find(
    (module) => module._type == SanityModuleType.BLOG_POST_BODY
  ) as SanityBlogPostBody | undefined

  const articleBody = blogPostBodyModule
    ? blocksToHtml({
        blocks: blogPostBodyModule.content?.filter(
          ({ _type }) =>
            _type === 'block' ||
            _type === 'quote' ||
            _type === 'figure' ||
            _type === 'infoBox'
        ),
        serializers: {
          types: {
            quote: ({ node: { text, author } }: { node: SanityContentQuote }) =>
              h('figure', {}, h('blockquote', text), h('figcaption', author)),
            figure: ({ node }: { node: SanityImageFragment }) => {
              const imageUrl = getSanityImageUrl(node, {
                width: node.dimensions.width,
                height: node.dimensions.height,
                fitMax: true,
                autoFormat: true,
              })

              return h('img', { src: imageUrl, alt: node.alt })
            },
            infoBox: ({
              node: { title, content },
            }: {
              node: SanityContentInfoBox
            }) => h('div', {}, h('h4', title), h('p', content)),
          },
        },
      })
    : ''

  const imageUrl = getSanityImageUrl(coverImage, {
    width: coverImage?.dimensions.width,
    height: coverImage?.dimensions.height,
    fitMax: true,
    autoFormat: true,
  })
  const thumbnailUrl = getSanityImageUrl(seo?.shareGraphic, {
    // Width and height taken from `HeadSeo` component
    width: 1200,
    height: 630,
  })

  const schema: BlogPostSchema = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline,
    abstract,
    articleBody,
    url: `${domain}${blogPostUrl}`,
    datePublished: new Date(date),
    dateModified: new Date(modifiedDate),
    keywords: categories?.map((category) => category.name),
    thumbnailUrl,
  }

  if (author) {
    schema.author = {
      '@type': 'Person',
      name: author.name,
      url: `${domain}${getPageUrl(PageType.BLOG_AUTHOR, author.slug)}`,
    }
  }

  if (imageUrl) {
    const imageWidth = coverImage?.dimensions.width
    const imageHeight = coverImage?.dimensions.height
    const image: ImageSchema = {
      '@type': 'ImageObject',
      '@id': imageUrl,
      url: imageUrl,
    }

    if (imageWidth) {
      image.width = `${imageWidth}`
    }
    if (imageHeight) {
      image.height = `${imageHeight}`
    }

    schema.image = image
  }

  return JSON.stringify(schema)
}

/**
 * Gets aggregate rating schema.
 */
export const getAggregateRatingSchema = (
  ratingCount?: number,
  ratingValue?: number
): AggregateRatingSchema | undefined => {
  if (!ratingCount || !ratingValue) {
    return
  }
  return {
    '@type': 'AggregateRating',
    ratingCount: `${ratingCount}`,
    ratingValue: `${ratingValue}`,
  }
}

/**
 * Gets reviews schema from given reviews.
 */
export const getReviewsSchema = (reviews: ProductReview[]) => {
  if (reviews.length === 0) {
    return
  }

  return reviews.map((review) => {
    const reviewSchema: ReviewSchema = {
      '@type': 'Review',
      reviewRating: {
        '@type': 'Rating',
        ratingValue: review.reviewRating,
      },
      author: {
        '@type': 'Person',
        name: review.author,
      },
      reviewBody: review.reviewMessage,
    }

    return reviewSchema
  })
}

/**
 * Returns product schema hook.
 */
export const useProductSchema = (
  site: SanitySiteFragment,
  product: SanityProductFragment,
  variant?: SanityProductVariantFragment
) => {
  const { currencyCode, taxRate, shopifyDomain } = useContext(ShopContext)

  const [ratingSchema, setRatingSchema] = useState<Rating>()

  // Load reviews and aggregate rating
  useEffect(() => {
    const loadRatingSchema = async () => {
      const options: ReviewOptions = {
        provider: ReviewProvider.STAMPED,
        apiKey: site.settings.stampedApiKey,
      }

      const reviews = await getProductReviews(
        shopifyDomain,
        product.id,
        options
      )
      const newReviewsSchema = getReviewsSchema(reviews)

      const productReviewSummary = await getProductReviewSummary(
        shopifyDomain,
        product.id,
        options
      )
      const newAggregateRating = getAggregateRatingSchema(
        productReviewSummary.ratingCount,
        productReviewSummary.ratingValue
      )

      setRatingSchema({
        reviews: newReviewsSchema,
        aggregateRating: newAggregateRating,
      })
    }

    loadRatingSchema()
  }, [product.id, shopifyDomain, site.settings.stampedApiKey])

  const productSchema = useMemo(() => {
    // Wait for Shop currency to load
    if (!currencyCode) {
      return
    }

    const description = product.description?.length
      ? blocksToHtml({ blocks: product.description })
      : ''

    const isInStock = variant?.inStock ?? product.inStock
    const price = getPrice(variant?.price ?? product.price, taxRate, false, '')
    const comparePrice = getPrice(
      variant?.comparePrice ?? product.comparePrice,
      taxRate,
      false,
      ''
    )
    const domain = site.settings.siteURL.replace(/\/+$/g, '')
    const productUrl = getPageUrl(PageType.PRODUCT, product.slug, {
      parameters: { variant: `${variant?.id}` },
    })
    const nextYearIsoDate = new Date(
      new Date().setFullYear(new Date().getFullYear() + 1)
    )
      .toISOString()
      .split('T')[0]

    const getImage = () => {
      if (!product.photos.main || !variant) {
        return
      }

      const photos = getProductGalleryPhotos(product.photos.main, variant)

      if (!photos) {
        return
      }

      return getSanityImageUrl(photos[0], {
        width: 800,
        fitMax: true,
        autoFormat: true,
      })
    }

    const newProductSchema: ProductSchema = {
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: product.title,
      description,
      sku: getFirstValue([variant?.sku, product.sku]),
      gtin: getFirstValue([variant?.barcode, product.barcode]),
      brand: {
        '@type': 'Brand',
        name: site.seo?.siteTitle ?? '',
      },
      image: getImage(),
      offers: {
        '@type': 'Offer',
        availability: `http://schema.org/${isInStock ? 'InStock' : 'SoldOut'}`,
        price: comparePrice && Number(comparePrice) > 0 ? comparePrice : price,
        priceCurrency: currencyCode,
        url: `${domain}${productUrl}`,
        priceValidUntil: nextYearIsoDate,
      },
      review: ratingSchema?.reviews,
      aggregateRating: ratingSchema?.aggregateRating,
    }

    return newProductSchema
  }, [
    currencyCode,
    product,
    ratingSchema,
    site.seo?.siteTitle,
    site.settings.siteURL,
    variant,
    taxRate,
  ])

  return JSON.stringify(productSchema)
}
